/**
 * HealthCoachScreen.jsx
 * Mobile App version of the KI-Gesundheitscoach
 * Provides AI-powered health coaching based on nutrition, training, and body data
 */
import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, Sparkles, Dumbbell, Apple, Activity, Scale, 
  ChevronDown, ChevronUp, MessageCircle, Send, TrendingUp,
  Brain, Target, Zap, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatStat } from '../../../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const HealthCoachScreen = ({ portalToken, customerId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [coaching, setCoaching] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [question, setQuestion] = useState('');
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [showQuestionInput, setShowQuestionInput] = useState(false);
  // Mirror Web-Portal: store user's last question so the answer is contextualised.
  const [lastQuestion, setLastQuestion] = useState('');

  useEffect(() => {
    if (portalToken && customerId) {
      loadCoaching();
    }
  }, [portalToken, customerId]);

  const loadCoaching = async (customQuestion = null) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/meal-assistant/health-coach/${customerId}`,
        { 
          focus_area: 'all',
          custom_question: customQuestion 
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setCoaching(response.data);
      if (customQuestion) {
        setLastQuestion(customQuestion);
        setQuestion('');
        setExpanded(true);
        setShowQuestionInput(false);
      } else {
        setLastQuestion('');
      }
    } catch (error) {
      console.error('Health coach error:', error);
      toast.error('Fehler beim Laden des KI-Coaches');
    } finally {
      setLoading(false);
      setAskingQuestion(false);
    }
  };

  const handleAskQuestion = () => {
    if (!question.trim()) return;
    setAskingQuestion(true);
    loadCoaching(question.trim());
  };

  const getStatusColor = (emoji) => {
    if (emoji === '🟢') return 'text-green-400';
    if (emoji === '🟡') return 'text-yellow-400';
    if (emoji === '🔴') return 'text-red-400';
    return 'text-yellow-400';
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/30 to-orange-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">KI-Gesundheitscoach</h1>
            <p className="text-zinc-400 text-xs">Personalisierte Tipps für dich</p>
          </div>
        </div>
        <button 
          onClick={() => loadCoaching()}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
          data-testid="refresh-coach-btn"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Loading State */}
      {loading && !coaching ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-yellow-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-zinc-400 text-sm">Analysiere deine Gesundheitsdaten...</p>
          <p className="text-zinc-500 text-xs mt-1">Training, Ernährung & Körper</p>
        </div>
      ) : coaching?.has_coaching ? (
        <>
          {/* Status Summary Card */}
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-2xl p-4 border border-yellow-500/30">
            <div className="flex items-start gap-3">
              <span className={`text-4xl ${getStatusColor(coaching.status_emoji)}`}>
                {coaching.status_emoji || '🟢'}
              </span>
              <div className="flex-1">
                <p className="text-white font-medium text-sm leading-relaxed">
                  {coaching.status_summary}
                </p>
              </div>
            </div>
          </div>

          {/* Data Summary Grid */}
          {coaching.data_summary && (
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                <Apple className="w-4 h-4 mx-auto mb-1 text-green-400" />
                <p className="text-white text-lg font-bold">
                  {formatStat(coaching.data_summary.nutrition_days)}
                </p>
                <p className="text-zinc-500 text-[10px]">Tage</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                <Dumbbell className="w-4 h-4 mx-auto mb-1 text-red-400" />
                <p className="text-white text-lg font-bold">
                  {formatStat(coaching.data_summary.total_workouts)}
                </p>
                <p className="text-zinc-500 text-[10px]">Trainings</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                <Activity className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                <p className="text-white text-lg font-bold">
                  {coaching.data_summary.avg_calories || '-'}
                </p>
                <p className="text-zinc-500 text-[10px]">Ø kcal</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                <Scale className="w-4 h-4 mx-auto mb-1 text-purple-400" />
                <p className="text-white text-lg font-bold">
                  {coaching.data_summary.weight || '-'}
                </p>
                <p className="text-zinc-500 text-[10px]">kg</p>
              </div>
            </div>
          )}

          {/* Strengths */}
          {coaching.strengths?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-green-400" />
                <h3 className="text-zinc-300 text-sm font-medium">Das läuft gut</h3>
              </div>
              <div className="space-y-2">
                {coaching.strengths.slice(0, expanded ? undefined : 2).map((strength, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-green-400 text-sm mt-0.5">✓</span>
                    <span className="text-white text-sm">{strength}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Areas to Improve */}
          {coaching.areas_to_improve?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-yellow-400" />
                <h3 className="text-zinc-300 text-sm font-medium">Verbesserungspotenzial</h3>
              </div>
              <div className="space-y-3">
                {coaching.areas_to_improve.slice(0, expanded ? undefined : 2).map((item, idx) => (
                  <div key={idx} className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-yellow-400 text-sm font-medium">{item.area}</p>
                    <p className="text-zinc-400 text-xs mt-1">{item.tip}</p>
                    {item.impact && (
                      <div className="flex items-center gap-1 mt-2 text-zinc-500">
                        <TrendingUp className="w-3 h-3" />
                        <span className="text-[10px]">{item.impact}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expanded Content */}
          {expanded && (
            <>
              {/* Synergy Insights */}
              {coaching.synergy_insights && (
                <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-yellow-400 text-sm font-medium">Zusammenspiel</h3>
                  </div>
                  <p className="text-white text-sm">{coaching.synergy_insights}</p>
                </div>
              )}

              {/* Quick Tips */}
              {coaching.quick_tips?.length > 0 && (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h3 className="text-zinc-300 text-sm font-medium mb-3">Schnelle Tipps</h3>
                  <div className="space-y-2">
                    {coaching.quick_tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-yellow-400">💡</span>
                        <span className="text-zinc-300 text-sm">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Question + Answer (Q&A Bubbles) */}
              {coaching.custom_answer && (
                <div className="rounded-xl border border-blue-500/20 overflow-hidden" data-testid="health-coach-qa-block">
                  {lastQuestion && (
                    <div className="px-3 py-2.5 bg-blue-500/10 border-b border-blue-500/20">
                      <div className="flex items-start gap-2">
                        <MessageCircle className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-400 mb-0.5">Deine Frage</p>
                          <p className="text-white text-sm">{lastQuestion}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="px-3 py-2.5 bg-zinc-800/40">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-yellow-400 mb-0.5">Antwort vom KI-Coach</p>
                        <p className="text-white text-sm whitespace-pre-line">{coaching.custom_answer}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Expand/Collapse Button */}
          {(coaching.areas_to_improve?.length > 2 || coaching.strengths?.length > 2 || coaching.synergy_insights || coaching.quick_tips) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full py-2 bg-zinc-800 rounded-lg text-zinc-400 text-sm flex items-center justify-center gap-2"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Weniger anzeigen
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Mehr anzeigen
                </>
              )}
            </button>
          )}

          {/* Ask Question */}
          {showQuestionInput ? (
            <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-400" />
                <h3 className="text-zinc-300 text-sm font-medium">Frage stellen</h3>
              </div>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="z.B. Wie kann ich mehr Protein zu mir nehmen?"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm placeholder-zinc-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowQuestionInput(false)}
                  className="flex-1 py-2 bg-zinc-700 rounded-lg text-zinc-300 text-sm"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAskQuestion}
                  disabled={askingQuestion || !question.trim()}
                  className="flex-1 py-2 bg-yellow-500 rounded-lg text-black text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {askingQuestion ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Fragen
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowQuestionInput(true)}
              className="w-full py-3 bg-zinc-800 rounded-xl text-zinc-300 text-sm flex items-center justify-center gap-2 border border-zinc-700"
              data-testid="ask-coach-btn"
            >
              <MessageCircle className="w-4 h-4 text-blue-400" />
              Frage an den Coach stellen
            </button>
          )}
        </>
      ) : (
        /* No Data State */
        <div className="bg-zinc-800/50 rounded-2xl p-6 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8 text-yellow-400" />
          </div>
          <div>
            <p className="text-white font-medium">Noch nicht genug Daten</p>
            <p className="text-zinc-400 text-sm mt-1">
              Tracke ein paar Tage deine Ernährung und Trainings, um personalisierte Tipps zu erhalten.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
              <Apple className="w-5 h-5 mx-auto text-green-400 mb-1" />
              <p className="text-zinc-500 text-xs">Ernährung</p>
            </div>
            <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
              <Dumbbell className="w-5 h-5 mx-auto text-red-400 mb-1" />
              <p className="text-zinc-500 text-xs">Training</p>
            </div>
            <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
              <Scale className="w-5 h-5 mx-auto text-purple-400 mb-1" />
              <p className="text-zinc-500 text-xs">Gewicht</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate?.('nutrition')}
            className="w-full py-3 bg-yellow-500 text-black rounded-xl font-medium"
          >
            Ernährung tracken
          </button>
        </div>
      )}
    </div>
  );
};

export default HealthCoachScreen;
